import { Color3, Mesh, VertexData, type Scene } from "@babylonjs/core";
import { CLIFF_HALF, CLIFF_SPACING, TAU, clamp, hash2, smoothstep, type Boulder, type CliffColumn, type Landmass } from "@tiny-seas/shared";
import { SURFACE_PROFILES } from "./materials.ts";

/**
 * Runtime sandstone for cliff-walled islands: stacked, chamfered blocks that sheath the
 * shared cliff colliders, fallen boulders and hanging vines. Geometry is flat-shaded
 * with world-planar metric UVs and baked linear vertex tints.
 */

const SANDSTONE = ["#d6a463", "#cf9b5a", "#e0b373", "#c48e52", "#d9aa6c", "#c99558"].map((hex) => Color3.FromHexString(hex));
const MOSS = ["#5f9a3e", "#4f8d3a", "#6aa548", "#557f35"].map((hex) => Color3.FromHexString(hex));
const ROCK_METRES = SURFACE_PROFILES.rock.metres;
const LEAF_METRES = SURFACE_PROFILES.leaf.metres;

type V3 = [number, number, number];

class Geometry {
  readonly positions: number[] = [];
  readonly colors: number[] = [];
  readonly uvs: number[] = [];
  readonly indices: number[] = [];

  /** One flat triangle wound to face `hint`, with planar UVs in `metres` repeats. */
  tri(a: V3, b: V3, c: V3, hint: V3, color: Color3, metres: number): void {
    // Babylon's ComputeNormals (and front-face culling): cross(a - b, c - b).
    const ux = a[0] - b[0], uy = a[1] - b[1], uz = a[2] - b[2];
    const vx = c[0] - b[0], vy = c[1] - b[1], vz = c[2] - b[2];
    let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    if (nx * hint[0] + ny * hint[1] + nz * hint[2] < 0) { [b, c] = [c, b]; nx = -nx; ny = -ny; nz = -nz; }
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len; ny /= len; nz /= len;
    // Horizontal U on walls, world X/Z on flats, so neighbouring faces share texel scale.
    let tx = 1, tz = 0;
    if (Math.abs(ny) < .9) { const h = Math.hypot(nx, nz); tx = -nz / h; tz = nx / h; }
    const bx = ny * tz, by = nz * tx - nx * tz, bz = -ny * tx;
    const base = this.positions.length / 3;
    for (const p of [a, b, c]) {
      this.positions.push(p[0], p[1], p[2]);
      this.uvs.push((p[0] * tx + p[2] * tz) / metres, (p[0] * bx + p[1] * by + p[2] * bz) / metres);
      this.colors.push(color.r, color.g, color.b, 1);
    }
    this.indices.push(base, base + 1, base + 2);
  }

  mesh(name: string, scene: Scene): Mesh {
    const normals: number[] = [];
    VertexData.ComputeNormals(this.positions, this.indices, normals);
    const data = new VertexData();
    data.positions = this.positions; data.indices = this.indices; data.normals = normals;
    data.uvs = this.uvs; data.colors = this.colors;
    const mesh = new Mesh(name, scene);
    data.applyToMesh(mesh);
    return mesh;
  }
}

interface Block {
  x: number;
  z: number;
  /** Outward unit direction of the block's depth axis. */
  nx: number;
  nz: number;
  width: number;
  depth: number;
  y0: number;
  y1: number;
  /** Horizontal shift of the top relative to the base, along the normal and the tangent. */
  lean: number;
  slant: number;
  /** Outward belly of the mid-height ring. */
  bulge: number;
  tint: Color3;
  seed: number;
}

const scratch = new Color3();

/** Sandstone shade at a world height: damp and shadowed near the sea, banded strata above. */
function stoneShade(tint: Color3, y: number, seed: number, faceUp: boolean, out: Color3): Color3 {
  const damp = 1 - .3 * (1 - smoothstep(-.4, .9, y));
  const strata = 1 + .06 * Math.sin(y * 2.3 + seed * 5.1) + .035 * Math.sin(y * 6.1 + seed);
  out.copyFrom(tint).scaleToRef(damp * strata * (faceUp ? 1.12 : 1), out);
  if (faceUp) out.set(out.r * 1.02, out.g * 1.01, out.b * .92);
  out.toLinearSpaceToRef(out);
  return out;
}

/** Chamfered octagonal prism, bellied at mid-height, leaning, with a bevelled uneven top. */
function pushBlock(g: Geometry, b: Block): void {
  const tx = b.nz, tz = -b.nx;
  const hw = b.width / 2, hd = b.depth / 2, k = Math.min(.8, b.width * .16, b.depth * .18);
  const footprint: [number, number][] = [[-hw + k, -hd], [hw - k, -hd], [hw, -hd + k], [hw, hd - k], [hw - k, hd], [-hw + k, hd], [-hw, hd - k], [-hw, -hd + k]];
  const height = b.y1 - b.y0, bevel = Math.min(.45, height * .18);
  const ring = (y: number, inset: number, salt: number, belly: number): V3[] => footprint.map(([u, v], i) => {
    const t = (y - b.y0) / height;
    const ju = (hash2(i, salt, b.seed) - .5) * .22, jv = (hash2(i, salt + 1, b.seed) - .5) * .22;
    const su = u * (1 - inset / hw) * (1 + belly / hw) + ju + b.slant * t;
    const sv = v * (1 - inset / hd) * (1 + belly / hd) + jv + b.lean * t;
    const jy = salt ? (hash2(i, salt + 2, b.seed) - .5) * Math.min(.2, height * .05) : 0;
    return [b.x + su * tx + sv * b.nx, y + jy, b.z + su * tz + sv * b.nz];
  });
  const mid = b.y0 + height * (.4 + hash2(1, 2, b.seed) * .2);
  const rings = [ring(b.y0, 0, 0, 0), ring(mid, 0, 13, b.bulge), ring(b.y1 - bevel, 0, 3, 0), ring(b.y1, bevel, 7, 0)];
  const centre: V3 = [b.x, (b.y0 + b.y1) / 2, b.z];
  const face = (p: V3[]) => {
    const fx = p.reduce((s, q) => s + q[0], 0) / p.length, fy = p.reduce((s, q) => s + q[1], 0) / p.length, fz = p.reduce((s, q) => s + q[2], 0) / p.length;
    const hint: V3 = [fx - centre[0], fy - centre[1], fz - centre[2]];
    const jitter = .93 + hash2(Math.round(fx * 7), Math.round(fz * 7), b.seed) * .14;
    const color = stoneShade(b.tint, fy, b.seed, false, scratch).scale(jitter);
    g.tri(p[0]!, p[1]!, p[2]!, hint, color, ROCK_METRES);
    g.tri(p[0]!, p[2]!, p[3]!, hint, color, ROCK_METRES);
  };
  for (let r = 0; r < rings.length - 1; r++) for (let i = 0; i < 8; i++) {
    const j = (i + 1) % 8;
    face([rings[r]![i]!, rings[r]![j]!, rings[r + 1]![j]!, rings[r + 1]![i]!]);
  }
  const crown = rings[3]!;
  const top: V3 = [crown.reduce((s, q) => s + q[0], 0) / 8, b.y1 + .05, crown.reduce((s, q) => s + q[2], 0) / 8];
  const capColor = stoneShade(b.tint, b.y1, b.seed, true, scratch).clone();
  for (let i = 0; i < 8; i++) g.tri(top, crown[i]!, crown[(i + 1) % 8]!, [0, 1, 0], capColor, ROCK_METRES);
}

export interface CliffDressing {
  /** World positions for cliff-face stake clusters, facing outward. */
  stakes: { x: number; y: number; z: number; yaw: number }[];
  vines: Mesh | null;
}

/** Rendered rock may reach this far outside the wall line: the collider plus the walker's radius. */
const MAX_REACH = CLIFF_HALF + .3;

/**
 * Massive sandstone courses over the exposed cliff walls. Each block spans two to
 * three collider columns and one stratum; faces stagger in and out but never
 * pass MAX_REACH, so walkers never clip into rendered rock.
 */
export function cliffMeshes(l: Landmass, columns: readonly CliffColumn[], scene: Scene): { rock: Mesh; dressing: CliffDressing } {
  const rock = new Geometry(), vines = new Geometry();
  const stakes: CliffDressing["stakes"] = [];
  let i = 0, block = 0;
  while (i < columns.length) {
    const seed = l.seed * 31 + block++;
    // Gather 2–3 contiguous columns into one buttress.
    const want = 2 + Math.floor(hash2(block, 1, seed) * 2);
    const run = [columns[i]!];
    while (run.length < want && i + run.length < columns.length) {
      const prev = run[run.length - 1]!, next = columns[i + run.length]!;
      if (Math.hypot(next.x - prev.x, next.z - prev.z) > CLIFF_SPACING * 1.6) break;
      run.push(next);
    }
    i += run.length;
    const n = run.length;
    const cx = run.reduce((s, c) => s + c.x, 0) / n, cz = run.reduce((s, c) => s + c.z, 0) / n;
    let nx = run.reduce((s, c) => s + c.nx, 0), nz = run.reduce((s, c) => s + c.nz, 0);
    const nl = Math.hypot(nx, nz); nx /= nl; nz /= nl;
    const base = Math.min(...run.map((c) => c.base)), top = run.reduce((s, c) => s + c.top, 0) / n;
    const bottom = base < 0 ? base - 1.5 : base - .8;
    const height = top - bottom;
    const span = n * CLIFF_SPACING;
    let y0 = bottom, course = 0;
    while (y0 < top - .5) {
      const remaining = top - y0;
      // Mostly 3–7 m strata, with the odd monolithic pillar.
      const tall = hash2(block, course, seed + 27) < .18 ? 8 + hash2(block, course, seed + 29) * 4 : 3.2 + hash2(block, course, seed + 3) * 3.6;
      const last = remaining < tall + 2;
      const y1 = last ? top + (hash2(block, 11 + course, seed) - .6) * .45 : y0 + tall;
      const width = span * (1.05 + hash2(block, course, seed + 17) * .3);
      const depth = 3.6 + hash2(block, course, seed + 5) * 1.2;
      const turn = (hash2(block, course, seed + 9) - .5) * Math.min(.4, 1.2 / width);
      const lean = (hash2(block, course, seed + 21) - .6) * .7, bulge = .1 + hash2(block, course, seed + 23) * .2;
      const slant = (hash2(block, course, seed + 25) - .5) * .8;
      const limit = MAX_REACH - Math.max(0, lean) - bulge - width / 2 * Math.sin(Math.abs(turn)) - .12;
      // Deep recesses read as cracks between buttresses; crowns stay close to the lip.
      const reach = Math.min(limit, last ? 1 + hash2(block, course, seed + 7) * .9 : .7 + hash2(block, course, seed + 7) * 1.6);
      const bx = nx * Math.cos(turn) - nz * Math.sin(turn), bz = nx * Math.sin(turn) + nz * Math.cos(turn);
      const slide = (hash2(block, course, seed + 13) - .5) * .9;
      const b: Block = {
        x: cx + bx * (reach - depth / 2) + nz * slide, z: cz + bz * (reach - depth / 2) - nx * slide, nx: bx, nz: bz,
        width, depth, y0, y1, lean: last ? Math.min(lean, 0) : lean, slant, bulge,
        tint: SANDSTONE[Math.floor(hash2(block, course, seed + 19) * SANDSTONE.length)]!, seed: seed + course * 101,
      };
      pushBlock(rock, b);
      // Moss and grass take hold on ledges left where the next course steps back.
      if (!last && y1 > .8 && hash2(block, course, seed + 31) < .4) {
        pushBlock(vines, { ...b, x: b.x + b.nz * b.slant + b.nx * b.lean, z: b.z - b.nx * b.slant + b.nz * b.lean,
          y0: y1 - .06, y1: y1 + .22 + hash2(block, course, seed + 33) * .2, width: width * .92, depth: depth * .9,
          lean: 0, slant: 0, bulge: .04, tint: MOSS[Math.floor(hash2(block, course, seed + 35) * MOSS.length)]!, seed: b.seed + 7 });
      }
      if (last && height > 7 && hash2(block, 23, seed) < .14) pushVine(vines, b, Math.min(height - 3, 2.5 + hash2(block, 29, seed) * 5));
      y0 = y1 - .25;
      course++;
    }
    if (height > 9 && block % 3 === 1) {
      const y = top - 3 - hash2(block, 31, seed) * Math.min(7, height - 6);
      stakes.push({ x: cx + nx * 1.8, y, z: cz + nz * 1.8, yaw: Math.atan2(nx, nz) });
    }
  }
  const rockMesh = rock.mesh(`cliffs:${l.id}`, scene);
  return { rock: rockMesh, dressing: { stakes, vines: vines.indices.length ? vines.mesh(`vines:${l.id}`, scene) : null } };
}

/** Ivy curtain hanging from a block's outer lip; double-sided, leaf-tinted. */
function pushVine(g: Geometry, b: Block, length: number): void {
  const tx = b.nz, tz = -b.nx, out = b.depth / 2 + .06;
  const strands = 2 + Math.floor(hash2(3, 5, b.seed) * 3);
  const leaf = Color3.FromHexString("#4f8d3a"), dark = Color3.FromHexString("#2f6a2e");
  for (let s = 0; s < strands; s++) {
    const u = (hash2(s, 1, b.seed) - .5) * b.width * .7;
    const drop = length * (.45 + hash2(s, 2, b.seed) * .55);
    const segs = Math.max(2, Math.round(drop / .5));
    for (let j = 0; j < segs; j++) {
      const ya = b.y1 + .05 - drop * j / segs, yb = b.y1 + .05 - drop * (j + 1) / segs;
      const wa = .22 * (1 - j / segs) + .05, wb = .22 * (1 - (j + 1) / segs) + .05;
      const sway = (hash2(s, j, b.seed + 41) - .5) * .25;
      const at = (uu: number, y: number): V3 => [b.x + tx * uu + b.nx * out, y, b.z + tz * uu + b.nz * out];
      const p0 = at(u - wa, ya), p1 = at(u + wa, ya), p2 = at(u + sway + wb, yb), p3 = at(u + sway - wb, yb);
      const tint = Color3.Lerp(leaf, dark, hash2(s, j, b.seed + 43)).toLinearSpace();
      for (const hint of [[b.nx, 0, b.nz], [-b.nx, 0, -b.nz]] as V3[]) {
        g.tri(p0, p1, p2, hint, tint, LEAF_METRES);
        g.tri(p0, p2, p3, hint, tint, LEAF_METRES);
      }
    }
  }
}

/** Tumbled blocks in the shallows: a leaning base stone capped by a smaller, turned one. */
export function boulderMesh(l: Landmass, boulders: readonly Boulder[], ground: (x: number, z: number) => number, scene: Scene): Mesh {
  const g = new Geometry();
  boulders.forEach((b, i) => {
    const x = l.cx + b.x, z = l.cz + b.z, seed = l.seed * 53 + i;
    const floor = ground(x, z) - 1;
    const split = b.r > 2.5 ? floor + (b.h - floor) * (.55 + hash2(i, 2, seed) * .15) : b.h;
    for (let k = 0; k < (split < b.h ? 2 : 1); k++) {
      const a = b.yaw + k * 1.1, scale = k ? .7 : 1;
      pushBlock(g, {
        x: x + (k ? Math.cos(a) * .3 : 0), z: z + (k ? Math.sin(a) * .3 : 0), nx: Math.cos(a), nz: Math.sin(a),
        width: b.r * 1.6 * scale * (.9 + hash2(k, 1, seed) * .15), depth: b.r * 1.35 * scale, y0: k ? split - .3 : floor, y1: k ? b.h : split,
        lean: (hash2(k, 3, seed) - .5) * .5, slant: (hash2(k, 4, seed) - .5) * .5, bulge: .12,
        tint: SANDSTONE[(i + k) % SANDSTONE.length]!, seed: seed + k * 7,
      });
    }
  });
  return g.mesh(`boulders:${l.id}`, scene);
}

/** One gull wing (side ±1) hinged at the origin, with half the body; the caller flaps it about z. */
export function gullWing(scene: Scene, side: number): Mesh {
  const g = new Geometry();
  const white = Color3.FromHexString("#f4f1ea").toLinearSpace(), tip = Color3.FromHexString("#3a3a40").toLinearSpace();
  const root: V3 = [0, 0, .12], back: V3 = [0, 0, -.18], mid: V3 = [side * .45, .06, 0], end: V3 = [side * .85, -.02, -.12];
  const head: V3 = [0, .04, .32], tail: V3 = [0, .03, -.3], flank: V3 = [side * .07, 0, 0], belly: V3 = [0, -.06, 0];
  for (const hint of [[0, 1, 0], [0, -1, 0]] as V3[]) {
    g.tri(root, mid, back, hint, white, LEAF_METRES);
    g.tri(mid, end, back, hint, tip, LEAF_METRES);
  }
  for (const [a, b] of [[head, flank], [flank, tail]] as const) {
    g.tri(a, b, [0, .09, 0], [side, 1, 0], white, LEAF_METRES);
    g.tri(a, b, belly, [side, -1, 0], white, LEAF_METRES);
  }
  return g.mesh(`gull-wing:${side}`, scene);
}

/** Wing beat for a gliding gull at time `t` (radians of wing lift). */
export function gullFlap(t: number, phase: number): number {
  const cycle = (t * .35 + phase) % 1;
  return cycle < .3 ? Math.sin(cycle / .3 * TAU) * .5 : clamp(.08 * Math.sin(t * 1.3 + phase * 9), -.1, .1);
}
